/* eslint-disable max-lines-per-function */
import dayjs from 'dayjs'
import type { UserActionsDetails, ModalField } from '../interfaces'
import {
  isOccurrenceConsultationReport,
  isDispatchTimelineReport,
  isOccurrenceDetailByTypeReport,
  isDispatchesByDeviceReport,
  isOccurrencesByDispatchGroupReport,
  isOccurrencesHandledByGroupReport,
  isDispatchByClosedStatusReport,
  isDispatchesStatusByDateReport,
  isDispatchesStatisticsByCommanderReport,
  isDispatchesByCommanderReport,
  isAllUsersReport,
  isDevicesReport,
  isDevicesByStatusReport,
  isDeviceServicesReport,
  isDevicesVsDispatchesReport,
  isConnectedDevicesReport,
  isDeviceTimelineReport,
  isOutOfServiceProductivityReport,
  isOnMissionProductivityReport,
  isEntryByStatusReport,
  isTotalEntryByUserReport,
  isTotalPrankCallsReport,
  isTotalEntryByAccuracyReport,
  isInterestPointReport,
  isConnectionLogsReport,
  isSearchCommentsReport,
  isQualifiersRecordsReport,
  isTotalQualifiersSearchesByUserReport,
  buildBaseUserFields,
  formatBoolean,
  extractObjectName,
} from './modalFieldHelpers'

// Funções auxiliares para reduzir duplicação
const formatArrayOfObjects = (
  arr: readonly unknown[] | null | undefined
): string => {
  if (!Array.isArray(arr) || !arr.length) return 'Não informado'
  return arr
    .map((item) => {
      if (typeof item === 'string') return item
      if (typeof item === 'object' && item !== null) {
        const name = (item as Record<string, unknown>)['name']
        return typeof name === 'string' ? name : JSON.stringify(item)
      }
      return String(item)
    })
    .filter(Boolean)
    .join(' • ')
}

const formatObject = (obj: unknown): string => {
  if (!obj) return 'Não informado'
  if (typeof obj === 'string') return obj
  if (typeof obj === 'object' && obj !== null) {
    const name = (obj as Record<string, unknown>)['name']
    return typeof name === 'string' ? name : JSON.stringify(obj)
  }
  return String(obj)
}

const formatPeriod = (
  startDate?: string | null,
  finishDate?: string | null
): string => {
  if (!startDate && !finishDate) return 'Não informado'
  const start = startDate ? dayjs(startDate).format('DD/MM/YYYY HH:mm') : ''
  const end = finishDate ? dayjs(finishDate).format('DD/MM/YYYY HH:mm') : ''
  return start && end ? `${start} - ${end}` : start || end
}

const formatPerformedAt = (createdAt?: string | null): string =>
  createdAt ? dayjs(createdAt).format('DD/MM/YYYY HH:mm') : 'Não informado'

const formatDateTime = (dateTime?: string | null): string =>
  dateTime ? dayjs(dateTime).format('DD/MM/YYYY HH:mm') : 'Não informado'

// Campos comuns reutilizáveis
const buildCommonFields = (
  details: UserActionsDetails,
  translate: (key: string) => string
) => ({
  agencies: {
    label: translate('Agency:name') || 'Agência',
    value: formatArrayOfObjects(details.agencies),
  },
  groupSet: {
    label: translate('Group set') || 'Agrupamento de Grupo de Despacho',
    value: formatObject(details.group_set),
  },
  dispatchGroups: {
    label: translate('Dispatch groups') || 'Grupo de Despacho',
    value: formatArrayOfObjects(details.dispatch_groups),
  },
  period: {
    label: translate('Period') || 'Período',
    value: formatPeriod(details.start_date, details.finish_date),
  },
  performedAt: {
    label: translate('Performed at') || 'Realizado em',
    value: formatPerformedAt(details.created_at),
  },
  city: {
    label: translate('City') || 'Cidade',
    value: details.city ?? 'Não informado',
  },
  neighborhood: {
    label: translate('Neighborhood') || 'Bairro',
    value: details.district ?? details.neighborhood ?? 'Não informado',
  },
  location: {
    label: translate('Location') || 'Localização',
    value: details.location ?? 'Não informado',
  },
  origin: {
    label: translate('Call origin') || 'Origem do Chamado',
    value: details.origin ?? 'Não informado',
  },
  operation: {
    label: translate('Operation') || 'Operação',
    value: details.operation?.name ?? 'Não informado',
  },
  category: {
    label: translate('Category') || 'Categoria',
    value: formatObject(details.category),
  },
  types: {
    label: translate('Type') || 'Tipo(s)',
    value: formatArrayOfObjects(details.types),
  },
  dateTime: {
    label: translate('Date/time') || 'Data do Momento',
    value: formatDateTime(details.date_time),
  },
  devices: {
    label: translate('Device:name') || 'Dispositivo',
    value: formatArrayOfObjects(details.devices),
  },
  contactCenter: {
    label: translate('Contact center') || 'Central de Contato',
    value: formatObject(details.contact_center),
  },
  entryOrigin: {
    label: translate('Entry origin') || 'Origem do Chamado',
    value: formatObject(details.entry_origin),
  },
})

export function getModalFields(
  selectedRow: unknown,
  translate: (key: string) => string
): readonly ModalField[] {
  if (!selectedRow) return []

  // Some search logs keep the report params inside `content` (string JSON or object)
  // so merge `content` when present so detectors and field readers can access
  // the same properties regardless of where API placed them.
  const raw = selectedRow as Record<string, unknown>
  let details = selectedRow as UserActionsDetails
  if (raw.content) {
    if (typeof raw.content === 'string') {
      try {
        const parsed = JSON.parse(raw.content) as Record<string, unknown>
        details = { ...(details as Record<string, unknown>), ...(parsed ?? {}) } as UserActionsDetails
      } catch {
        // ignore parse errors
      }
    } else if (typeof raw.content === 'object' && raw.content !== null) {
      details = { ...(details as Record<string, unknown>), ...(raw.content as Record<string, unknown>) } as UserActionsDetails
    }
  }

  // Relatório: Consulta de Ocorrência (exibir apenas campos solicitados)
  if (isOccurrenceConsultationReport(details)) {
    return [
      {
        label: translate('Start date') || 'Data Inicial',
        value: formatDateTime(details.start_date),
      },
      {
        label: translate('Finish date') || 'Data Final',
        value: formatDateTime(details.finish_date),
      },
      {
        label: translate('Group set') || 'Agrupamento de Grupo de Despacho',
        value: formatObject(details.group_set),
      },
      {
        label: translate('Dispatch groups') || 'Grupo de Despacho',
        value: formatArrayOfObjects(details.dispatch_groups),
      },
      {
        label: translate('Phone') || 'Telefone',
        value: details.phone ?? 'Não informado',
      },
      {
        label: translate('Call origin') || 'Origem do Chamado',
        value: details.origin ?? 'Não informado',
      },
      {
        label: translate('City') || 'Cidade',
        value: details.city ?? 'Não informado',
      },
      {
        label: translate('Neighborhood') || 'Bairro',
        value: details.neighborhood ?? 'Não informado',
      },
      {
        label: translate('Location') || 'Localização',
        value: details.location ?? 'Não informado',
      },
      {
        label: translate('Operation') || 'Operação',
        value: details.operation?.name ?? 'Não informado',
      },
      { label: translate('Type') || 'Tipo', value: formatObject(details.type) },
      {
        label: translate('Subtype') || 'Subtipo',
        value: formatObject(details.subtype),
      },
    ]
  }

  // Relatório: Cronologia de Ocorrência (dispatch timeline)
  if (isDispatchTimelineReport(details)) {
    const baseFields = buildBaseUserFields(details, translate)
    return [
      baseFields[0], // user
      baseFields[3], // station
      baseFields[1], // report
      {
        label: translate('Period') || 'Período',
        value: formatPeriod(details.start_date, details.finish_date),
      },
      {
        label: translate('Dispatch') || 'Despacho',
        value: extractObjectName(details.dispatch),
      },
      baseFields[4], // performed_at
    ]
  }

  // Relatório: Ocorrências por tipo (occurrence-detail-by-type)
  if (isOccurrenceDetailByTypeReport(details)) {
    return [
      {
        label: translate('Agency:name') || 'Agência',
        value: formatArrayOfObjects(details.agencies),
      },
      {
        label: translate('Group set') || 'Agrupamento de Grupo de Despacho',
        value: formatObject(details.group_set),
      },
      {
        label: translate('Dispatch groups') || 'Grupo de Despacho',
        value: formatArrayOfObjects(details.dispatch_groups),
      },
      {
        label: translate('Category') || 'Categoria',
        value: formatObject(details.category),
      },
      { label: translate('Type') || 'Tipo', value: formatObject(details.type) },
      {
        label: translate('City') || 'Cidade',
        value: details.city ?? 'Não informado',
      },
      {
        label: translate('Neighborhood') || 'Bairro',
        value: details.district ?? details.neighborhood ?? 'Não informado',
      },
      {
        label: translate('Location') || 'Localização',
        value: details.location ?? 'Não informado',
      },
      {
        label: translate('Call origin') || 'Origem do Chamado',
        value: details.origin ?? 'Não informado',
      },
      {
        label: translate('Origin type') || 'Tipo de Origem',
        value: formatObject(details.origin_type),
      },
      {
        label: translate('Operation') || 'Operação',
        value: details.operation?.name ?? 'Não informado',
      },
      {
        label: translate('Period') || 'Período',
        value: formatPeriod(details.start_date, details.finish_date),
      },
      {
        label: translate('Performed at') || 'Realizado em',
        value: formatPerformedAt(details.created_at),
      },
    ]
  }

  // Relatório: Ocorrências por dispositivo (/report/dispatches-by-device)
  if (isDispatchesByDeviceReport(details)) {
    const common = buildCommonFields(details, translate)
    const deviceLabel = extractObjectName(
      details.action_device ?? (details as { readonly device?: unknown }).device
    )
    return [
      { label: translate('Device:name') || 'Dispositivo', value: deviceLabel },
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.origin,
      common.city,
      common.neighborhood,
      common.location,
      common.operation,
      common.period,
      { label: translate('Type') || 'Tipo', value: formatObject(details.type) },
      {
        label: translate('Subtype') || 'Subtipo',
        value: formatObject(details.subtype),
      },
      common.performedAt,
    ]
  }

  // Relatório: Ocorrências por Grupo de Despacho e tipo (/report/occurrences)
  if (isOccurrencesByDispatchGroupReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.category,
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.origin,
      common.city,
      common.neighborhood,
      common.location,
      {
        label: translate('Closed status') || 'Status de Fechamento',
        value: formatObject(details.closed_status),
      },
      common.operation,
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Totalização de ocorrência por grupo de despacho (/report/occurrences-handled-by-group)
  if (isOccurrencesHandledByGroupReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.types,
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.city,
      common.neighborhood,
      common.location,
      common.origin,
      common.operation,
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Ocorrências por data específica (/report/dispatches-status-by-date)
  if (isDispatchesStatusByDateReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.dateTime,
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.category,
      common.types,
      common.city,
      common.neighborhood,
      {
        label: translate('Location') || 'Localização',
        value: details.location || 'Não informado',
      },
      common.origin,
      common.performedAt,
    ]
  }

  // Relatório: Total de ocorrências por fechamento (/report/dispatch-by-closed-status)
  if (isDispatchByClosedStatusReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.category,
      common.types,
      common.city,
      common.neighborhood,
      common.location,
      common.origin,
      common.operation,
      {
        label: translate('Closed status class') || 'Classe de Fechamento',
        value: details.closed_status_class ?? 'Não informado',
      },
      {
        label: translate('Closed status') || 'Status de Fechamento',
        value: details.closed_status ?? 'Não informado',
      },
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Ocorrências por Comandante (/report/dispatches-statistics-by-commander)
  // Handle the older /report/dispatches-by-commander payloads which may appear
  // in logs as either top-level or inside `content`.
  if (isDispatchesByCommanderReport(details) || isDispatchesStatisticsByCommanderReport(details)) {
    const baseFields = buildBaseUserFields(details, translate)
    const common = buildCommonFields(details, translate)
    return [
      baseFields[0], // user
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.period,
      baseFields[4], // performed_at
    ]
  }

  // Relatório: Lista de Dispositivos (/report/devices)
  if (isDevicesReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.performedAt,
    ]
  }

  // Relatório: Atendimento das Viaturas (/report/device-services)
  if (isDeviceServicesReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Cronologia do Dispositivo (/report/device-timeline)
  if (isDeviceTimelineReport(details)) {
    const common = buildCommonFields(details, translate)
    const deviceText =
      details.action_device ??
      (details as { readonly device?: unknown }).device ??
      null
    const deviceLabel = deviceText
      ? typeof deviceText === 'string'
        ? deviceText
        : extractObjectName(deviceText)
      : 'Não informado'

    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      { label: translate('Device:name') || 'Dispositivo', value: deviceLabel },
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Produtividade - Status Fora de Serviço (/report/out-of-service-productivity)
  if (isOutOfServiceProductivityReport(details)) {
    const common = buildCommonFields(details, translate)
    const deviceText =
      details.action_device ??
      (details as { readonly device?: unknown }).device ??
      null
    const deviceLabel = deviceText
      ? typeof deviceText === 'string'
        ? deviceText
        : extractObjectName(deviceText)
      : 'Não informado'

    return [
      { label: translate('Device:name') || 'Dispositivo', value: deviceLabel },
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      {
        label: translate('Out of service reason') || 'Motivo Fora de Serviço',
        value: details.out_of_service_reason ?? 'Não informado',
      },
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Produtividade - Status Em Missão (/report/on-mission-productivity)
  if (isOnMissionProductivityReport(details)) {
    const common = buildCommonFields(details, translate)
    const deviceText =
      details.action_device ??
      (details as { readonly device?: unknown }).device ??
      null
    const deviceLabel = deviceText
      ? typeof deviceText === 'string'
        ? deviceText
        : extractObjectName(deviceText)
      : 'Não informado'

    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      { label: translate('Device:name') || 'Dispositivo', value: deviceLabel },
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Teleatendimentos por status e usuário (/report/entry-by-status)
  if (isEntryByStatusReport(details)) {
    const common = buildCommonFields(details, translate)
    const baseFields = buildBaseUserFields(details, translate)
    const stationsText =
      Array.isArray(details.stations) && details.stations.length
        ? details.stations
            .map((stationItem) => {
              if (typeof stationItem === 'string') return stationItem
              if (typeof stationItem === 'object' && stationItem !== null) {
                const rec = stationItem as Record<string, unknown>
                return (rec['name'] as string) ?? JSON.stringify(stationItem)
              }
              return String(stationItem)
            })
            .join(' • ')
        : 'Não informado'

    return [
      {
        label: translate('Entry status') || 'Status de Atendimento',
        value: formatObject(details.entry_status),
      },
      common.contactCenter,
      baseFields[0], // user
      { label: translate('Stations') || 'Estações', value: stationsText },
      common.entryOrigin,
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Total de atendimentos por usuário (/report/total-entry-by-user)
  if (isTotalEntryByUserReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.contactCenter,
      common.entryOrigin,
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Total de trotes registrados (/report/total-prank-calls)
  if (isTotalPrankCallsReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.contactCenter,
      common.entryOrigin,
      {
        label: translate('Phone') || 'Telefone',
        value: details.phone ?? 'Não informado',
      },
      {
        label: translate('Prank origin') || 'Origem do Trote',
        value: details.origin ?? 'Não informado',
      },
      common.period,
      common.performedAt,
    ]
  }

  // Relatório: Total de atendimentos por acurácia de endereço e usuário (/report/total-entry-by-accuracy)
  if (isTotalEntryByAccuracyReport(details)) {
    const common = buildCommonFields(details, translate)
    return [common.contactCenter, common.period, common.performedAt]
  }

  // Relatório: Pontos de interesse registrados (/report/interest-point)
  if (isInterestPointReport(details)) {
    const nameText = details.name ?? 'Não informado'
    const notesText = details.notes ?? 'Não informado'

    const areasText =
      Array.isArray(details.areas) && details.areas.length
        ? details.areas
            .map((areaItem) => {
              if (typeof areaItem === 'string') return areaItem
              if (typeof areaItem === 'object' && areaItem !== null) {
                const rec = areaItem as Record<string, unknown>
                return (rec['name'] as string) ?? JSON.stringify(areaItem)
              }
              return String(areaItem)
            })
            .join(' • ')
        : 'Não informado'

    const typeText = details.interest_point_type
      ? typeof details.interest_point_type === 'object' &&
        details.interest_point_type !== null
        ? ((details.interest_point_type as Record<string, unknown>)[
            'name'
          ] as string) ?? JSON.stringify(details.interest_point_type)
        : String(details.interest_point_type)
      : 'Não informado'

    const subtypeText = details.interest_point_subtype
      ? typeof details.interest_point_subtype === 'object' &&
        details.interest_point_subtype !== null
        ? ((details.interest_point_subtype as Record<string, unknown>)[
            'name'
          ] as string) ?? JSON.stringify(details.interest_point_subtype)
        : String(details.interest_point_subtype)
      : 'Não informado'

    // Status derivado dos flags
    let statusText = 'Não informado'
    if (details.expired) statusText = 'Expirado'
    else if (details.need_review) statusText = 'Pendente de Revisão'
    else if (details.not_validated) statusText = 'Não validado'
    else statusText = 'Validado'

    const createdValue =
      details.created_start_date || details.created_finish_date
        ? `${
            details.created_start_date
              ? dayjs(details.created_start_date).format('DD/MM/YYYY HH:mm')
              : ''
          }${
            details.created_start_date && details.created_finish_date
              ? ' - '
              : ''
          }${
            details.created_finish_date
              ? dayjs(details.created_finish_date).format('DD/MM/YYYY HH:mm')
              : ''
          }`
        : 'Não informado'

    const reviewValue =
      details.review_start_date || details.review_finish_date
        ? `${
            details.review_start_date
              ? dayjs(details.review_start_date).format('DD/MM/YYYY HH:mm')
              : ''
          }${
            details.review_start_date && details.review_finish_date ? ' - ' : ''
          }${
            details.review_finish_date
              ? dayjs(details.review_finish_date).format('DD/MM/YYYY HH:mm')
              : ''
          }`
        : 'Não informado'

    const expiredValue =
      details.expired_start_date || details.expired_finish_date
        ? `${
            details.expired_start_date
              ? dayjs(details.expired_start_date).format('DD/MM/YYYY HH:mm')
              : ''
          }${
            details.expired_start_date && details.expired_finish_date
              ? ' - '
              : ''
          }${
            details.expired_finish_date
              ? dayjs(details.expired_finish_date).format('DD/MM/YYYY HH:mm')
              : ''
          }`
        : 'Não informado'

    const performedAt = details.created_at
      ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm')
      : 'Não informado'

    return [
      { label: translate('Name') || 'Nome', value: nameText },
      { label: translate('Notes') || 'Observações', value: notesText },
      { label: translate('Areas') || 'Áreas', value: areasText },
      {
        label: translate('Interest point type') || 'Tipo de Ponto de Interesse',
        value: typeText,
      },
      {
        label:
          translate('Interest point subtype') ||
          'Subtipo de Ponto de Interesse',
        value: subtypeText,
      },
      { label: translate('Status') || 'Status', value: statusText },
      {
        label: translate('Creation date') || 'Data de Criação',
        value: createdValue,
      },
      {
        label: translate('Review date') || 'Data de Revisão',
        value: reviewValue,
      },
      {
        label: translate('Expiration date') || 'Data de Expiração',
        value: expiredValue,
      },
      {
        label: translate('Performed at') || 'Realizado em',
        value: performedAt,
      },
    ]
  }

  // Relatório: Status histórico de dispositivo (/report/devices-by-status)
  if (isDevicesByStatusReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.devices,
      common.dateTime,
      common.performedAt,
    ]
  }

  // Relatório: Status das Viaturas x Ocorrências pendentes (/report/devices-vs-dispatches)
  if (isDevicesVsDispatchesReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.dateTime,
      common.performedAt,
    ]
  }

  // Relatório: Total de dispositivos conectados (/report/connected-devices)
  if (isConnectedDevicesReport(details)) {
    const common = buildCommonFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.dateTime,
      common.performedAt,
    ]
  }

  // Relatório: Registro de Conexões (/report/connection-logs)
  if (isConnectionLogsReport(details)) {
    const common = buildCommonFields(details, translate)
    const baseFields = buildBaseUserFields(details, translate)
    return [
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.devices,
      baseFields[0], // user
      common.city,
      common.operation,
      common.period,
      baseFields[4], // performed_at
    ]
  }

  // Relatório: Pesquisa por múltiplos comentários (/report/search-comments)
  if (isSearchCommentsReport(details)) {
    const common = buildCommonFields(details, translate)
    let keywordsText = 'Não informado'
    if (Array.isArray(details.key_words) && details.key_words.length) {
      keywordsText = details.key_words.filter(Boolean).join(' • ')
    }

    return [
      {
        label: translate('Key words') || 'Palavras chaves',
        value: keywordsText,
      },
      common.period,
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
      common.category,
      common.types,
      common.city,
      common.neighborhood,
      common.location,
      common.origin,
      common.operation,
      common.performedAt,
    ]
  }

  // Relatório: Registro de Qualificadores (/report/qualifiers-records-by-options)
  if (isQualifiersRecordsReport(details)) {
    const common = buildCommonFields(details, translate)
    const baseFields = buildBaseUserFields(details, translate)

    // Mapear qualifier_options de IDs para labels
    const qualifierLabels: Record<number, string> = {
      0: 'Com veículo informado',
      1: 'Com pessoa informada',
      2: 'Com droga apreendida',
      3: 'Com armamento apreendido',
      4: 'Com objeto apreendido',
      5: 'Com organização informado',
      6: 'Com animal informado',
    }

    let qualifiersText = 'Não informado'
    const qualifierOptions = details.qualifier_options
    if (Array.isArray(qualifierOptions) && qualifierOptions.length) {
      const mappedQualifiers = qualifierOptions
        .map((qualItem) => {
          if (typeof qualItem === 'number')
            return qualifierLabels[qualItem] ?? `ID: ${qualItem}`
          if (typeof qualItem === 'string') return qualItem
          if (typeof qualItem === 'object' && qualItem !== null) {
            const qRec = qualItem as Record<string, unknown>
            const maybeName = qRec['label'] ?? qRec['name'] ?? qRec['value']
            if (typeof maybeName === 'string' && maybeName.trim())
              return maybeName
            return JSON.stringify(qualItem)
          }
          return String(qualItem)
        })
        .filter(Boolean)

      if (mappedQualifiers.length) qualifiersText = mappedQualifiers.join(' • ')
    }

    return [
      baseFields[0], // user
      baseFields[3], // station
      baseFields[1], // report
      common.period,
      baseFields[4], // performed_at
      {
        label:
          translate('Selected qualifiers') || 'Qualificadores selecionados',
        value: qualifiersText,
        size: 3,
      },
      { label: 'CSV', value: formatBoolean(details.export_csv, translate) },
      common.agencies,
      common.groupSet,
      common.dispatchGroups,
    ]
  }

  // Relatório: Total de pesquisas de qualificadores por usuário (/report/total-qualifiers-searches-by-user)
  if (isTotalQualifiersSearchesByUserReport(details)) {
    const agenciesText =
      Array.isArray(details.agencies) && details.agencies.length
        ? details.agencies
            .map((agencyItem) =>
              typeof agencyItem === 'object' && agencyItem !== null
                ? ((agencyItem as Record<string, unknown>)['name'] as string) ??
                  JSON.stringify(agencyItem)
                : String(agencyItem)
            )
            .join(' • ')
        : 'Não informado'

    // Permissão / grupo de permissão (vários possíveis nomes na payload)
    const permissionGroupRaw =
      (details as Record<string, unknown>)['permission_groups'] ??
      (details as Record<string, unknown>)['group'] ??
      (details as Record<string, unknown>)['group_id'] ??
      null

    let permissionGroupText = 'Não informado'
    if (Array.isArray(permissionGroupRaw) && permissionGroupRaw.length) {
      permissionGroupText = permissionGroupRaw
        .map((groupItem) =>
          typeof groupItem === 'object' && groupItem !== null
            ? ((groupItem as Record<string, unknown>)['name'] as string) ??
              JSON.stringify(groupItem)
            : String(groupItem)
        )
        .join(' • ')
    } else if (permissionGroupRaw && typeof permissionGroupRaw === 'object') {
      permissionGroupText =
        ((permissionGroupRaw as Record<string, unknown>)['name'] as string) ??
        JSON.stringify(permissionGroupRaw)
    } else if (permissionGroupRaw != null) {
      permissionGroupText = String(permissionGroupRaw)
    }

    // Período: preferir o campo numérico de quantidade (active_in_last / activeInLast) se existir,
    // caso contrário usar start_date/finish_date
    const activeQty =
      (details as Record<string, unknown>)['active_in_last'] ??
      (details as Record<string, unknown>)['activeInLast'] ??
      (details as Record<string, unknown>)['value'] ??
      null

    const periodValue =
      activeQty != null
        ? String(activeQty)
        : details.start_date || details.finish_date
        ? `${
            details.start_date
              ? dayjs(details.start_date).format('DD/MM/YYYY HH:mm')
              : ''
          }${details.start_date && details.finish_date ? ' - ' : ''}${
            details.finish_date
              ? dayjs(details.finish_date).format('DD/MM/YYYY HH:mm')
              : ''
          }`
        : 'Não informado'

    const performedAt = details.created_at
      ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm')
      : 'Não informado'

    return [
      { label: translate('Agency:name') || 'Agência', value: agenciesText },
      {
        label: translate('Permission Group') || 'Grupo de Permissão',
        value: permissionGroupText,
      },
      { label: translate('Period') || 'Período', value: periodValue },
      {
        label: translate('Performed at') || 'Realizado em',
        value: performedAt,
      },
    ]
  }

  // Relatório: Lista de Usuários (/report/all-users)
  if (isAllUsersReport(details)) {
    const agenciesText =
      Array.isArray(details.agencies) && details.agencies.length
        ? details.agencies
            .map((agencyItem) =>
              typeof agencyItem === 'object' && agencyItem !== null
                ? ((agencyItem as Record<string, unknown>)['name'] as string) ?? JSON.stringify(agencyItem)
                : String(agencyItem)
            )
            .join(' • ')
        : 'Não informado'

    const permissionGroupRaw =
      (details as Record<string, unknown>)['permission_groups'] ??
      (details as Record<string, unknown>)['group'] ??
      (details as Record<string, unknown>)['group_id'] ??
      null

    let permissionGroupText = 'Não informado'
    if (Array.isArray(permissionGroupRaw) && permissionGroupRaw.length) {
      permissionGroupText = permissionGroupRaw
        .map((groupItem) =>
          typeof groupItem === 'object' && groupItem !== null
            ? ((groupItem as Record<string, unknown>)['name'] as string) ?? JSON.stringify(groupItem)
            : String(groupItem)
        )
        .join(' • ')
    } else if (permissionGroupRaw && typeof permissionGroupRaw === 'object') {
      permissionGroupText = ((permissionGroupRaw as Record<string, unknown>)['name'] as string) ?? JSON.stringify(permissionGroupRaw)
    } else if (permissionGroupRaw != null) {
      permissionGroupText = String(permissionGroupRaw)
    }

    // Prefer numeric active quantity when provided
    const activeQty =
      (details as Record<string, unknown>)['active_in_last'] ??
      (details as Record<string, unknown>)['activeInLast'] ??
      (details as Record<string, unknown>)['value'] ??
      null

    const periodValue = activeQty != null ? String(activeQty) : 'Não informado'

    const performedAt = details.created_at ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm') : 'Não informado'

    return [
      { label: translate('Agency:name') || 'Agência', value: agenciesText },
      { label: translate('Permission Group') || 'Grupo de Permissão', value: permissionGroupText },
      { label: translate('Period') || 'Período', value: periodValue },
      { label: translate('Performed at') || 'Realizado em', value: performedAt },
    ]
  }

  // Fallback para relatórios não identificados
  return []
}

export default getModalFields
